import React from 'react';
import { RectElement } from '../../../core/template.schemas';
import { useTemplateStore } from '../../hooks/useTemplateStore';
import { Label } from '@/ui-components/ui/label';
import { Input } from '@/ui-components/ui/input';
import { Square } from 'lucide-react';
import { CommonProperties } from './common';
import { AdvancedStaticDynamicSelector, elementAttributeRegistry } from '../../registry';
import { FillSelector } from './FillSelector';

interface RectPropertiesProps {
  element: RectElement;
  onOpenSlotPanel?: () => void;
}

/**
 * Rectangle Properties Component
 * Displays rectangle-specific properties like fill, stroke and corner radius
 */
export const RectProperties: React.FC<RectPropertiesProps> = ({ element, onOpenSlotPanel }) => {
  const updateElement = useTemplateStore(s => s.updateElement);

  // Get attribute mappings for rect elements
  const rectMapping = elementAttributeRegistry.get('rect');

  const strokeColorAttribute = rectMapping?.attributes.find(attr => attr.attributeKey === 'stroke.color');

  const handlePropertyChange = (property: keyof RectElement, value: any) => {
    updateElement(element.id, { [property]: value });
  };

  // Removed unused handleFillColorChange and getCurrentFillColor

  const handleStrokeColorChange = (colorValue: any) => {
    const currentStroke = element.stroke || { 
      color: strokeColorAttribute?.defaultValue || { mode: 'static', mediaType: 'color', staticValue: '#000000' }, 
      width: 1 
    };
    handlePropertyChange('stroke', { ...currentStroke, color: colorValue });
  };

  const handleStrokeWidthChange = (width: number) => {
    const currentStroke = element.stroke || { 
      color: strokeColorAttribute?.defaultValue || { mode: 'static', mediaType: 'color', staticValue: '#000000' }, 
      width: 1 
    };
    handlePropertyChange('stroke', { ...currentStroke, width });
  };

  // Helper function to get current stroke color as StaticOrDynamic
  const getCurrentStrokeColor = () => {
    if (element.stroke?.color) {
      return element.stroke.color; // element.stroke.color ist bereits ein ColorValue
    }
    return strokeColorAttribute?.defaultValue || {
      mode: 'static' as const,
      mediaType: 'color' as const,
      staticValue: '#000000'
    };
  };

  return (
    <div className="space-y-4">
      <CommonProperties element={element} />
      
      {/* Rectangle-specific Properties */}
      <div className="space-y-4 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <Square className="w-3.5 h-3.5 text-gray-500" />
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Rechteck</Label>
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          {/* Corner Radius */}
          <div className="space-y-1.5">
            <Label className="text-[11px] text-gray-500 font-medium">Eckenradius</Label>
            <div className="relative">
              <Input
                type="number"
                value={element.cornerRadius || 0}
                onChange={(e) => handlePropertyChange('cornerRadius', parseFloat(e.target.value) || 0)}
                className="h-8 text-[11px] rounded-lg border-gray-200 pr-7"
                min="0"
              />
              <span className="text-[10px] text-gray-400 absolute right-2 top-2 font-medium">px</span>
            </div>
          </div>

          {/* Stroke Width */}
          {element.stroke && (
            <div className="space-y-1.5">
              <Label className="text-[11px] text-gray-500 font-medium">Rahmenbreite</Label>
              <div className="relative">
                <Input
                  type="number"
                  value={element.stroke.width}
                  onChange={(e) => handleStrokeWidthChange(parseFloat(e.target.value) || 0)}
                  className="h-8 text-[11px] rounded-lg border-gray-200 pr-7"
                  min="0"
                  step="0.5"
                />
                <span className="text-[10px] text-gray-400 absolute right-2 top-2 font-medium">px</span>
              </div>
            </div>
          )}
        </div>
        
        {/* Fill Selector */}
        <div className="bg-gray-50/30 p-2 rounded-xl border border-gray-100">
          <FillSelector 
            value={element.fill} 
            onChange={(fill) => handlePropertyChange('fill', fill)}
            onOpenSlotPanel={onOpenSlotPanel || (() => {})}
          />
        </div>
        
        {/* Stroke Color */}
        {element.stroke && strokeColorAttribute && (
          <div className="bg-gray-50/30 p-2 rounded-xl border border-gray-100">
            <AdvancedStaticDynamicSelector
              label="Rahmenfarbe"
              value={getCurrentStrokeColor()}
              onChange={handleStrokeColorChange}
              compatibleMediaTypes={strokeColorAttribute.compatibleMediaTypes}
              required={strokeColorAttribute.required}
              onOpenSlotPanel={onOpenSlotPanel}
            />
          </div>
        )}
      </div>
    </div>
  );
};